import { useCallback, useMemo } from 'react';
import { useStore } from 'react-redux';
import { AnchorEffectAssignValueEntry, AnchorEntry, BelongsToPopulatedValue, CanvasPosition, Entry, EntityType, NoteEntry, PropertyDefinition, ReferenceEntry } from '../types';
import { CanvasEntryLayoutRow } from '../utils/canvasGridUtils';
import {
	ANCHOR_SNAP_EPSILON,
	ANCHOR_SNAP_THRESHOLD,
	AnchorLineSpec,
	applyAnchorSnap,
	buildAnchorLineSpecs,
	getAnchorDragTilt as getAnchorDragTiltUtil,
	getSnappedAnchorId as getSnappedAnchorIdUtil,
} from '../utils/canvasAnchorUtils';
import { useAppDispatch } from '../hooks';
import { repositoryEntriesApi, useGetRepositoryByIdQuery, useRepositoryEntryUpdateMutation } from '../services/repositoryEntries';
import { repositoryEntryUpdateLocalAction, selectAllEntryProperties } from '../containers/contentSlice';
import type { RootState } from '../store';
import { mergePropertyDefinitions } from '../utils/resolveGridAxisSnap';
import { repositoryEntriesApiRowToPopulatedEntry } from '../common/util';

type UseCanvasAnchorsArgs = {
  visibleEntries: Entry[];
  entriesWithCanvasPositions: CanvasEntryLayoutRow[];
  allPropertyDefinitions: PropertyDefinition[];
  anchorEffects: AnchorEffectAssignValueEntry[];
  isAuthenticated: boolean;
  draggingReferenceRepositoryId: string | null;
  isDraggingNote: boolean;
  getPosition: (entry: Entry, index: number) => CanvasPosition;
};

export default function useCanvasAnchors({
	visibleEntries,
	entriesWithCanvasPositions,
	allPropertyDefinitions,
	anchorEffects,
	isAuthenticated,
	draggingReferenceRepositoryId,
	isDraggingNote,
	getPosition,
}: UseCanvasAnchorsArgs) {
	const dispatch = useAppDispatch();
	const store = useStore<RootState>();
	const [repositoryEntryUpdateMutation] = useRepositoryEntryUpdateMutation();

	const autoAssignRepositoryId = useMemo(() => {
		if (!anchorEffects.length) return '';
		const effectAnchorIds = new Set(anchorEffects.map((effect) => effect.anchorId));
		const anchor = visibleEntries.find(
			(e) => e.type === EntityType.Anchor && effectAnchorIds.has(e.id),
		) as AnchorEntry | undefined;
		return anchor?.repositoryId ?? '';
	}, [anchorEffects, visibleEntries]);

	const { data: repositoryForAutoAssign } = useGetRepositoryByIdQuery(autoAssignRepositoryId, {
		skip: !isAuthenticated || !autoAssignRepositoryId,
	});

	const propertyDefinitionsForAutoAssign = useMemo(
		() => mergePropertyDefinitions(allPropertyDefinitions, repositoryForAutoAssign?.propertyDefinitions),
		[allPropertyDefinitions, repositoryForAutoAssign?.propertyDefinitions],
	);

	const anchorEntriesWithPositions = useMemo(
		() =>
			visibleEntries
				.map((entry, index) => ({ entry, index }))
				.filter(
					({ entry }) =>
						entry.type === EntityType.Anchor &&
            Boolean((entry as AnchorEntry).repositoryId),
				)
				.map(({ entry, index }) => {
					const anchor = entry as AnchorEntry;
					// Reference-attached anchors inherit their position from the reference card.
					const position = anchor.referenceId
						? (() => {
							const row = entriesWithCanvasPositions.find(
								(r) => r.entry.id === anchor.referenceId,
							);
							return row ? { x: row.x, y: row.y } : (anchor.xy ?? getPosition(entry, index));
						})()
						: (anchor.xy ?? getPosition(entry, index));
					return { id: entry.id, anchor, position };
				}),
		[entriesWithCanvasPositions, getPosition, visibleEntries],
	);

	const snappedWithPos = useMemo(
		() =>
			entriesWithCanvasPositions
				.filter(({ entry }) => entry.type === EntityType.Reference || entry.type === EntityType.Note)
				.map(({ entry, x, y }) => ({ entry: entry as ReferenceEntry | NoteEntry, x, y })),
		[entriesWithCanvasPositions],
	);

	const anchorAxisLines: AnchorLineSpec[] = useMemo(
		() =>
			buildAnchorLineSpecs({
				anchorEntries: anchorEntriesWithPositions,
				snappedWithPos,
				draggingRepositoryId: draggingReferenceRepositoryId,
				isDraggingNote,
				epsilon: ANCHOR_SNAP_EPSILON,
			}),
		[anchorEntriesWithPositions, draggingReferenceRepositoryId, isDraggingNote, snappedWithPos],
	);

	const isDraggingAnchorReference = useMemo(
		() => anchorAxisLines.some((line) => line.isActive),
		[anchorAxisLines],
	);

	const constrainDragPositionForAnchors = useCallback(
		(entry: Entry, x: number, y: number): CanvasPosition =>
			applyAnchorSnap(entry, { x, y }, anchorAxisLines, ANCHOR_SNAP_THRESHOLD),
		[anchorAxisLines],
	);

	const getSnappedAnchorId = useCallback(
		(entry: Entry, x: number, y: number): string | null =>
			getSnappedAnchorIdUtil(entry, { x, y }, anchorAxisLines, ANCHOR_SNAP_EPSILON),
		[anchorAxisLines],
	);

	const getAnchorDragTilt = useCallback(
		(entry: Entry, x: number, y: number): number =>
			getAnchorDragTiltUtil(entry, { x, y }, anchorAxisLines, ANCHOR_SNAP_EPSILON),
		[anchorAxisLines],
	);

	const getCurrentRelationIds = useCallback(
		(collectionEntryId: string, def: PropertyDefinition): string[] => {
			const state = store.getState();
			if (isAuthenticated) {
				const { data } = repositoryEntriesApi.endpoints.loadRepositoryEntry3.select(collectionEntryId)(state);
				const row = data?.rows[0];
				if (!row) return [];
				const cells = (repositoryEntriesApiRowToPopulatedEntry(row).data[def.slug] ?? []) as BelongsToPopulatedValue[];
				return cells.map((cell) => cell.value);
			}
			return selectAllEntryProperties(state)
				.filter((prop) => prop.parentId === collectionEntryId && prop.slug === def.slug)
				.map((prop) => String(prop.value));
		},
		[isAuthenticated, store],
	);

	const syncAnchorAutoAssignFromPosition = useCallback(
		async (movedEntry: Entry, x: number, y: number) => {
			if (movedEntry.type !== EntityType.Reference) return;
			const ref = movedEntry as ReferenceEntry;

			for (const line of anchorAxisLines) {
				if (line.repositoryId !== ref.repositoryId) continue;

				const isSnapped =
					line.direction === 'vertical'
						? Math.abs(x - line.coord) <= ANCHOR_SNAP_EPSILON
						: Math.abs(y - line.coord) <= ANCHOR_SNAP_EPSILON;
				if (!isSnapped) continue;

				const collectionEntryId = ref.repositoryEntryId;
				if (!collectionEntryId) continue;

				const effectsForAnchor = anchorEffects.filter((effect) => effect.anchorId === line.anchorId);
				for (const effect of effectsForAnchor) {
					const def = propertyDefinitionsForAutoAssign.find((d) => d.id === effect.propertyId);
					if (!def || def.type !== EntityType.PropertyDefinitionBelongsTo) continue;

					const currentIds = getCurrentRelationIds(collectionEntryId, def);
					if (currentIds.length === 1 && currentIds[0] === effect.value) continue;

					const nextValues = [effect.value];
					if (isAuthenticated) {
						const target = await dispatch(
							repositoryEntriesApi.endpoints.loadRepositoryEntry3.initiate(effect.value, { subscribe: false }),
						).unwrap();
						repositoryEntryUpdateMutation({
							id: collectionEntryId,
							values: { [def.slug]: nextValues },
							optimisticValues: { [def.slug]: [{ value: effect.value, label: target.rows[0].label }] },
							repositoryId: ref.repositoryId,
						});
					} else {
						dispatch(repositoryEntryUpdateLocalAction({ id: collectionEntryId, values: { [def.slug]: nextValues } }));
					}
				}
				break;
			}
		},
		[propertyDefinitionsForAutoAssign, anchorAxisLines, anchorEffects, dispatch, getCurrentRelationIds, isAuthenticated, repositoryEntryUpdateMutation],
	);

	return {
		anchorAxisLines,
		isDraggingAnchorReference,
		constrainDragPositionForAnchors,
		getSnappedAnchorId,
		getAnchorDragTilt,
		syncAnchorAutoAssignFromPosition,
	};
}
